import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import type { SavedQueryPreviewResponse } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';

interface Props {
  queryId: string;
  toolKey: 'db.save_query' | 'excel.save_query';
  open: boolean;
  onClose: () => void;
}

/**
 * 저장된 쿼리의 결과를 즉시 실행해 1000행 × 100컬럼 까지 미리 보여준다.
 *
 * 메모리 누수 방지:
 * - useQuery 의 signal 을 fetch 에 전달 — 모달 unmount 시 in-flight 요청 abort.
 * - gcTime: 30_000 — 모달 닫고 30초 후 캐시에서 데이터 GC. 1000×100 결과는 무시 못 할 크기라
 *   기본 5분(default)보다 짧게.
 * - 결과를 별도 state 로 복제하지 않음. data 는 react-query cache 만 의존.
 * - ESC / 배경 클릭 / X 닫기는 Modal.tsx 의 useEffect cleanup 으로 listener 자동 해제.
 */
export function SavedQueryPreviewModal({ queryId, toolKey, open, onClose }: Props) {
  const query = useQuery<SavedQueryPreviewResponse, Error>({
    queryKey: ['saved-query-preview', queryId],
    queryFn: ({ signal }) => api.previewSavedQuery(queryId, signal),
    enabled: open,
    staleTime: 0,
    gcTime: 30_000,
    retry: false,
  });

  const title = toolKey === 'db.save_query' ? '쿼리 미리보기' : '엑셀 쿼리 미리보기';

  return (
    <Modal open={open} onClose={onClose} title={title} size="xl">
      <div className="space-y-3">
        {query.isPending ? (
          <div className="py-8 text-center text-sm text-foreground-muted">실행 중…</div>
        ) : query.isError ? (
          <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {query.error instanceof ApiError ? query.error.message : '미리보기 실행 실패'}
          </div>
        ) : query.data ? (
          <PreviewBody preview={query.data} />
        ) : null}
      </div>
      <div className="flex justify-end pt-3">
        <Button variant="ghost" onClick={onClose}>
          닫기
        </Button>
      </div>
    </Modal>
  );
}

function PreviewBody({ preview }: { preview: SavedQueryPreviewResponse }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-muted">
        <span className="font-mono">{preview.rowCount}행</span>
        <span className="font-mono">· {preview.columns.length}컬럼</span>
        {preview.truncated ? (
          <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-mono text-amber-700 dark:text-amber-400">
            1000행 이상 — 자름
          </span>
        ) : null}
        {preview.truncatedColumns ? (
          <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-mono text-amber-700 dark:text-amber-400">
            100컬럼 이상 — 처음 100개만 표시
          </span>
        ) : null}
        {preview.injectedLimit ? (
          <span className="rounded bg-surface-muted px-1.5 py-0.5 text-[10px] font-mono">
            LIMIT 자동 주입
          </span>
        ) : null}
        <span className="font-mono">· {preview.latencyMs}ms</span>
      </div>
      <div className="rounded border border-border bg-surface overflow-auto max-h-[70vh]">
        {preview.columns.length === 0 ? (
          <div className="p-3 text-xs text-foreground-muted">빈 결과</div>
        ) : (
          <table className="w-full text-[11px]">
            <thead className="bg-surface-muted/40 sticky top-0 z-10">
              <tr>
                {preview.columns.map((c) => (
                  <th
                    key={c}
                    className="px-2 py-1.5 text-left font-medium font-mono uppercase text-foreground-muted whitespace-nowrap"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((row, ri) => (
                <tr
                  // biome-ignore lint/suspicious/noArrayIndexKey: preview 결과 정적 렌더
                  key={ri}
                  className="border-t border-border"
                >
                  {row.map((cell, ci) => (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: column index 는 컬럼 순서와 일치
                      key={ci}
                      className="px-2 py-1 font-mono whitespace-nowrap align-top"
                      title={formatCell(cell)}
                    >
                      {formatCell(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}
